import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, LayoutDashboard, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { moduleBySlug } from '@/lib/modules';

// Main landing has hash links + dedicated section routes
const HASH_LINKS = [
  { hash: '/#modules', label: 'Modules' },
  { hash: '/#pricing', label: 'Plans' },
];

const ABOUT_DROPDOWN = [
  { href: '/about/about-attp', label: 'About ATTP Corporate Hub' },
  { href: '/about/mission-vision', label: 'Mission & Vision' },
  { href: '/about/core-values', label: 'Core Values' },
  { href: '/about/lead-consultant', label: 'Lead Consultant & Founder' },
  { href: '/about/countries-of-operation', label: 'Countries of Operation' },
  { href: '/about/why-choose-us', label: 'Why Choose Us' },
  { href: '/about/our-approach', label: 'Our Approach' },
  { href: '/about/contact-us', label: 'Contact Us' },
];

const DIVISIONS_DROPDOWN = [
  { href: '/engineering', label: 'Engineering Division', sub: 'Olu-Akintade Computer & Electrical Works' },
  { href: '/corporate', label: 'ATTP Corporate Hub', sub: 'Consulting · Training · Education' },
];

export default function MainNav({ variant = 'light' }) {
  const [open, setOpen] = useState(false);
  const [openDrop, setOpenDrop] = useState(null);
  const dark = variant === 'dark';
  const { user } = useAuth();
  const { pathname } = useLocation();

  const role = user?.role;
  const firstValidModule = (user?.modules || []).find((s) => moduleBySlug(s));
  const dashTarget = firstValidModule ? `/dashboard/${firstValidModule}` : '/team';
  const isPrivileged = role === 'super_admin' || role === 'org_manager';

  const linkBase = `px-3.5 py-2 text-[14px] font-medium tracking-tight rounded-md transition-colors`;
  const linkColor = dark ? 'text-white/85 hover:text-white hover:bg-white/10' : 'text-neutral-700 hover:text-navy hover:bg-neutral-100';

  return (
    <header
      className={`sticky top-0 z-40 w-full backdrop-blur-xl border-b ${dark ? 'bg-black/65 border-white/10' : 'bg-white/85 border-neutral-200/70'}`}
      data-testid="main-nav"
    >
      <div className="container-x flex items-center justify-between h-20 lg:h-24">
        <Link to="/" className="flex items-center gap-3 group" data-testid="brand-logo-link">
          <div className="h-14 w-14 lg:h-16 lg:w-16 rounded-md flex items-center justify-center p-1.5 transition-colors" style={{ background: dark ? 'rgba(255,255,255,0.06)' : 'transparent' }}>
            <img src="/logos/attp-main.png" alt="ATTP NEXUS" className="h-full w-full object-contain" />
          </div>
          <div className="leading-tight">
            <div className={`font-display text-base lg:text-lg font-bold tracking-tight ${dark ? 'text-white' : 'text-navy'}`}>
              ATTP <span className="text-amber">NEXUS</span>
            </div>
            <div className={`text-[10px] lg:text-[11px] uppercase tracking-[0.22em] ${dark ? 'text-white/55' : 'text-neutral-500'}`}>
              African Techno-Technological Pathway
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="primary-nav">
          <Link to="/" className={`${linkBase} ${linkColor}`}>Home</Link>

          {/* Divisions dropdown */}
          <DropdownTrigger label="Divisions" open={openDrop === 'div'} onOpen={() => setOpenDrop('div')} onClose={() => setOpenDrop(null)} dark={dark} testId="nav-divisions">
            <div className="w-80 py-2">
              {DIVISIONS_DROPDOWN.map((d) => {
                const active = pathname === d.href || pathname.startsWith(d.href + '/');
                return (
                  <Link key={d.href} to={d.href}
                    className="block px-5 py-3 hover:bg-neutral-50 transition-colors border-l-2"
                    style={{ borderLeftColor: active ? '#D4A437' : 'transparent', color: active ? '#0F2A5C' : undefined }}
                    data-testid={`nav-div-${d.href.replace('/', '')}`}
                  >
                    <div className="text-sm font-semibold text-navy">{d.label}</div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">{d.sub}</div>
                  </Link>
                );
              })}
            </div>
          </DropdownTrigger>

          {/* About Us dropdown */}
          <DropdownTrigger label="About Us" open={openDrop === 'about'} onOpen={() => setOpenDrop('about')} onClose={() => setOpenDrop(null)} dark={dark} testId="nav-about-us">
            <div className="w-72 py-2">
              {ABOUT_DROPDOWN.map((item) => {
                const active = pathname === item.href;
                return (
                  <Link key={item.href} to={item.href}
                    className="block px-5 py-2.5 text-sm hover:bg-neutral-50 transition-colors border-l-2"
                    style={{ borderLeftColor: active ? '#D4A437' : 'transparent', color: active ? '#0F2A5C' : '#525252', fontWeight: active ? 600 : 500 }}
                    data-testid={`nav-about-${item.href.split('/').pop()}`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </DropdownTrigger>

          {HASH_LINKS.map((l) => (
            <a key={l.hash} href={l.hash} className={`${linkBase} ${linkColor}`} data-testid={`nav-${l.label.toLowerCase()}`}>
              {l.label}
            </a>
          ))}
          <a href="/#contact" className={`${linkBase} ${linkColor}`} data-testid="nav-contact">Contact</a>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <Link to={dashTarget} className={dark ? 'btn-ghost-dark' : 'btn-outline'} data-testid="nav-dashboard-btn">
                <LayoutDashboard className="w-4 h-4" />
                {isPrivileged ? 'Dashboard' : 'My Dashboard'}
              </Link>
              {role === 'super_admin' && (
                <Link to="/control-panel" className="btn-accent" data-testid="nav-control-panel-btn">
                  <ShieldCheck className="w-4 h-4" /> Control Panel
                </Link>
              )}
              {role === 'org_manager' && (
                <Link to="/team" className="btn-accent" data-testid="nav-team-btn">
                  <ShieldCheck className="w-4 h-4" /> Team
                </Link>
              )}
            </>
          ) : (
            <Link to="/login" className="btn-accent" data-testid="nav-login-btn">
              Login
            </Link>
          )}
        </div>

        <button className={`lg:hidden p-2 ${dark ? 'text-white' : 'text-navy'}`} onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="mobile-menu-toggle">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className={`lg:hidden border-t ${dark ? 'bg-black border-white/10' : 'bg-white border-neutral-200'}`}>
          <div className="container-x py-4 flex flex-col gap-1.5">
            <Link to="/" className="text-sm font-medium py-2 px-2" onClick={() => setOpen(false)}>Home</Link>
            <details className="text-sm">
              <summary className="font-medium py-2 px-2 cursor-pointer">Divisions</summary>
              <div className="pl-4 flex flex-col gap-1 mt-1">
                {DIVISIONS_DROPDOWN.map((d) => <Link key={d.href} to={d.href} className="text-sm py-1.5 px-2 text-neutral-600" onClick={() => setOpen(false)}>{d.label}</Link>)}
              </div>
            </details>
            <details className="text-sm">
              <summary className="font-medium py-2 px-2 cursor-pointer">About Us</summary>
              <div className="pl-4 flex flex-col gap-1 mt-1">
                {ABOUT_DROPDOWN.map((i) => <Link key={i.href} to={i.href} className="text-sm py-1.5 px-2 text-neutral-600" onClick={() => setOpen(false)}>{i.label}</Link>)}
              </div>
            </details>
            {HASH_LINKS.map((l) => (
              <a key={l.hash} href={l.hash} className="text-sm font-medium py-2 px-2" onClick={() => setOpen(false)}>{l.label}</a>
            ))}
            <a href="/#contact" className="text-sm font-medium py-2 px-2" onClick={() => setOpen(false)}>Contact</a>
            {user ? (
              <>
                <Link to={dashTarget} className="btn-outline mt-2" onClick={() => setOpen(false)}>
                  {isPrivileged ? 'Dashboard' : 'My Dashboard'}
                </Link>
                {role === 'super_admin' && <Link to="/control-panel" className="btn-accent" onClick={() => setOpen(false)}>Control Panel</Link>}
                {role === 'org_manager' && <Link to="/team" className="btn-accent" onClick={() => setOpen(false)}>Team</Link>}
              </>
            ) : (
              <Link to="/login" className="btn-accent" onClick={() => setOpen(false)}>Login</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function DropdownTrigger({ label, open, onOpen, onClose, dark, testId, children }) {
  return (
    <div className="relative" onMouseEnter={onOpen} onMouseLeave={onClose}>
      <button
        className={`px-3.5 py-2 text-[14px] font-medium tracking-tight rounded-md transition-colors inline-flex items-center gap-1 ${dark ? 'text-white/85 hover:text-white hover:bg-white/10' : 'text-neutral-700 hover:text-navy hover:bg-neutral-100'}`}
        data-testid={testId}
      >
        {label} <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-md shadow-2xl border border-neutral-200 z-50">
          {children}
        </div>
      )}
    </div>
  );
}
